import type { Finding, Severity, MsPillar } from '../api/client';
import { PILLARS, SEVERITY_ORDER } from './pillars';

/**
 * Posture scoring.
 *
 * A number on a dashboard invites more trust than it deserves, so the basis is
 * stated wherever the score appears and the maths is deliberately simple enough
 * to explain in a sentence.
 *
 * What this score is: a weighted count of what this assessment found, relative
 * to the control outcomes it is capable of measuring.
 *
 * What it is not: a benchmark against other organisations, a compliance
 * percentage, or evidence that anything unmeasured is sound. A control with no
 * finding may be a pass or a check that never ran, and the score cannot tell
 * the difference.
 */

/** Relative cost of a finding. A critical is not five lows. */
const WEIGHT: Record<Severity, number> = {
  Critical: 10,
  High: 6,
  Medium: 3,
  Low: 1,
  Info: 0,
};

export interface PostureScore {
  /** 0 to 100. Higher is better. */
  score: number;
  band: 'Strong' | 'Reasonable' | 'Weak' | 'Poor';
  /** Weighted total of everything found. */
  weighted: number;
  /** Control outcomes this assessment can measure. */
  measurable: number;
  /** Control outcomes that raised at least one finding. */
  affected: number;
  basis: string;
}

export function scorePosture(findings: Finding[], measurableCount: number): PostureScore {
  const weighted = findings.reduce((sum, f) => sum + (WEIGHT[f.severity] ?? 0), 0);

  const affected = new Set(
    findings.filter((f) => f.primitiveId).map((f) => f.primitiveId),
  ).size;

  // Normalised against the worst realistic case: every measurable outcome
  // carrying a high severity finding. Anchoring on Critical would make almost
  // every real tenant look excellent, which flatters rather than informs.
  const ceiling = Math.max(1, measurableCount * WEIGHT.High);
  const score = Math.max(0, Math.round(100 - (weighted / ceiling) * 100));

  const band: PostureScore['band'] =
    score >= 85 ? 'Strong' : score >= 70 ? 'Reasonable' : score >= 50 ? 'Weak' : 'Poor';

  return {
    score,
    band,
    weighted,
    measurable: measurableCount,
    affected,
    basis:
      `Weighted from ${findings.length} finding(s) against ${measurableCount} measurable ` +
      `control outcomes. Critical counts 10, high 6, medium 3, low 1, informational 0. ` +
      `This is a relative indicator of what was found, not a compliance percentage or ` +
      `a benchmark against other organisations.`,
  };
}

export interface PillarPosture {
  pillar: MsPillar;
  label: string;
  route: string;
  total: number;
  bySeverity: Record<Severity, number>;
  worst: Severity | null;
  measurable: number;
  affected: number;
}

/** Per pillar breakdown, for the heatmap. */
export function pillarPosture(
  findings: Finding[],
  pillarOf: (primitiveId: string | null) => MsPillar | null,
  measurableByPillar: Record<string, number>,
): PillarPosture[] {
  return PILLARS.map((p) => {
    const inPillar = findings.filter((f) => pillarOf(f.primitiveId) === p.id);
    const bySeverity = SEVERITY_ORDER.reduce<Record<string, number>>((acc, s) => {
      acc[s] = inPillar.filter((f) => f.severity === s).length;
      return acc;
    }, {}) as Record<Severity, number>;

    return {
      pillar: p.id,
      label: p.label,
      route: p.route,
      total: inPillar.length,
      bySeverity,
      worst: SEVERITY_ORDER.find((s) => (bySeverity[s] ?? 0) > 0) ?? null,
      measurable: measurableByPillar[p.id] ?? 0,
      affected: new Set(inPillar.filter((f) => f.primitiveId).map((f) => f.primitiveId)).size,
    };
  });
}

/**
 * The findings a consultant should raise first.
 *
 * Ordered by severity, then by how many entities are affected. A critical
 * touching forty accounts is a different conversation from one touching a
 * single service principal, and the ordering should say so.
 */
export function topRisks(findings: Finding[], limit = 5): Finding[] {
  return [...findings]
    .filter((f) => f.severity === 'Critical' || f.severity === 'High')
    .sort(
      (a, b) =>
        SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) ||
        b.resources.length - a.resources.length ||
        a.checkId.localeCompare(b.checkId),
    )
    .slice(0, limit);
}

/**
 * A 0 to 100 score per pillar, for the radar chart.
 *
 * Same weighting as the overall score, normalised against the outcomes that
 * pillar can measure rather than the whole catalogue. Without that, a pillar
 * with three measurable outcomes and one critical would look healthier than a
 * pillar with fifteen outcomes and one medium.
 */
export function pillarScores(rows: PillarPosture[]): number[] {
  return rows.map((r) => {
    const weighted = SEVERITY_ORDER.reduce(
      (sum, s) => sum + (r.bySeverity[s] ?? 0) * (WEIGHT[s] ?? 0), 0);
    const ceiling = Math.max(1, r.measurable * WEIGHT.High);
    return Math.max(0, Math.round(100 - (weighted / ceiling) * 100));
  });
}
